import React, { useEffect, useState } from "react";
import { Error, Layout, MainCanvas } from "./components";
import Loader from "./components/SceneObjects/Loader/Loader";
import { ThemeProvider } from "@mui/material/styles";
import { ErrorType } from "./store/types/store";
import { useStoreRoom, useStoreSystem } from "./store";
import { Utils } from "./utils/utils";
import { API } from "./controllers";
import { theme } from "./Theme";
import shallow from "zustand/shallow";
import { useCurrentSearchParam } from "./utils/hooks/useCurrentSearchParam";
import { useNavigate } from "react-router-dom";
import EditSectionModal from "./components/Modals/EditSectionModal/EditSectionModal";
import EditIndentModal from "./components/Modals/EditIndentModal/EditIndentModal";
import FillingModal from "./components/Modals/FillingModal/FillingModal";
import DecorsModal from "./components/Modals/FurnitureModal/DecorsModal";
import EditDecorModal from "./components/Modals/EditDecorModal/EditDecorModal";
import { useWindowProperty } from "./utils/hooks/useWidnowProperty";
import { getWallLineVertices } from "./components/SceneObjects/ButtonsScene/utils";
import { getCameraPositionToMiddleLine, getTargetPositionToMiddleLine } from "./store/helpers";
import { useHDR } from "./hooks";
import { SaveAndAuthProjectModal } from "./components/Modals/SaveAndAuthProjectModal/SaveAndAuthProjectModal";
import { calcRoomDepth } from "./utils";
import { SavedProjectModal } from "./components/Modals/SavedProjectModal/SavedProjectModal";
import PartsModal from "./components/Modals/PartsModal/PartsModal";
import { TireRacksModal } from "./components/Modals/TireRacksModal/TireRacksModal";
import EditTireRackModal from "./components/Modals/EditTireRackModal/EditTireRackModal";
import { enrichItemsWithNomenclature } from "./store/helpers/enrichItemsWithNomenclature";
import { enrichConstructionsWithNomenclature } from "./store/helpers/enrichConstructionsWithNomenclature";

function App() {
    const [error, setError] = useState<ErrorType>({ isExist: false, message: "" });
    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const navigate = useNavigate();
    const [hash, , hashForUrl] = useCurrentSearchParam("hash");
    const [isPro] = useCurrentSearchParam("Pro");
    // debugger;
    useHDR();

    const {
        sectionEditModal,
        itemEditModal,
        isOpenSaveProjectModal,
        updateItemsGroup,
        decorsModal,
        loadProject,
        currentDecorId,
        setFurnitureData,
        currentTireRackId,
        savedProjectModal,
        tireRacksModal,
        setConstructions,
        proModeFromState,
        setSystemProperties,
    } = useStoreSystem(
        (state) => ({
            sectionEditModal: state.sectionEditModal,
            itemEditModal: state.itemEditModal,
            updateItemsGroup: state.updateItemsGroup,
            decorsModal: state.decorsModal,
            loadProject: state.loadProject,
            currentDecorId: state.currentDecorId,
            setFurnitureData: state.setOriginDecors,
            setSystemProperties: state.setSystemProperties,
            isOpenSaveProjectModal: state.saveProjectModal.isOpen,
            savedProjectModal: state.savedProjectModal,
            proModeFromState: state.proMode,
            setConstructions: state.setConstructions,
            tireRacksModal: state.tireRacksModal,
            currentTireRackId: state.currentTireRackId,
        }),
        shallow
    );
    const { setDepth, depth, length, height, setCameraDistance } = useStoreRoom(
        (state) => ({
            setDepth: state.setDepth,
            depth: state.depth,
            length: state.length,
            height: state.height,
            setCameraDistance: state.setCameraDistance,
        }),
        shallow
    );

    useEffect(() => {
        if (Utils.isTouchDevice()) setError({ isExist: true, message: "Конфигуратор работает с настольного ПК" });
        const lineVertices = getWallLineVertices("front");
        setSystemProperties([
            { name: "cameraPosition", value: getCameraPositionToMiddleLine(lineVertices) },
            { name: "targetCameraPosition", value: getTargetPositionToMiddleLine(lineVertices) },
        ]);
        /*if (document.location.pathname.includes('app-pro')){
            setSystemProperties([
                { name: 'proMode', value: true },
            ])
        }*/
    }, []);

    useEffect(() => {
        const proMode = (isPro && isPro === "true") || proModeFromState;
        setSystemProperties([{ name: "proMode", value: proMode }]);
        if (!proMode && !hash) {
            const proModeEverActivated = useStoreSystem.getState().proModeEverActivated;

            if (!proModeEverActivated) {
                // Используем старую логику с depth
                setDepth(calcRoomDepth(Math.max(length, depth), height));
            } else {
                // Используем новую логику с cameraDistance
                setCameraDistance(calcRoomDepth(Math.max(length, depth), height));
            }
        }
    }, [proModeFromState, isPro, hash]);

    useEffect(() => {
        (async function () {
            const { data } = await API.Configurator.getFillings();
            const modulesResponse = await API.Configurator.getModules();
            const constructionsResponse = await API.Configurator.getConstructions();
            setFurnitureData(modulesResponse.data);

            const enrichedItemsGroup = await enrichItemsWithNomenclature(Object.values(data));
            const enrichedConstructions = await enrichConstructionsWithNomenclature(constructionsResponse.data);

            setConstructions(enrichedConstructions);

            if (hash) {
                const status = await loadProject(hash);
                if (status === "fail") {
                    navigate(window.location.pathname.replace(hashForUrl, ""));
                }
            }

            setIsLoaded(true);
            updateItemsGroup(enrichedItemsGroup);
        })();
    }, []);

    if (error.isExist) {
        return <Error text={error.message} />;
    }

    return (
        <ThemeProvider theme={theme}>
            {isLoaded ? (
                <Layout>
                    <>
                        <MainCanvas />
                        {tireRacksModal.isOpen && tireRacksModal.category && <TireRacksModal />}
                        {sectionEditModal.isOpen && sectionEditModal.section && sectionEditModal.type === "edit" && (
                            <EditSectionModal editSection={sectionEditModal.section} />
                        )}
                        {sectionEditModal.isOpen && sectionEditModal.section && sectionEditModal.type === "indent" && (
                            <EditIndentModal editSection={sectionEditModal.section} />
                        )}
                        {sectionEditModal.isOpen && sectionEditModal.section && sectionEditModal.type === "fill" && (
                            <FillingModal editSection={sectionEditModal.section} />
                        )}
                        {itemEditModal.isOpen && itemEditModal.item && itemEditModal.section && (
                            <PartsModal editItem={itemEditModal.item} editSection={itemEditModal.section} />
                        )}
                        {decorsModal.isOpen && decorsModal.category && <DecorsModal category={decorsModal.category} />}
                        {isOpenSaveProjectModal && <SaveAndAuthProjectModal />}
                        {currentDecorId && <EditDecorModal />}
                        {currentTireRackId && <EditTireRackModal />}
                        <SavedProjectModal />
                    </>
                </Layout>
            ) : (
                <Loader isLoading={!isLoaded} />
            )}
        </ThemeProvider>
    );
}

export default App;
